/**
 * LoginModal - Login/Signup modal overlay for the main app
 */

import { useState, useCallback, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/utils/cn';
import { LoginForm } from './LoginForm';
import { SignupForm } from './SignupForm';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'login' | 'signup';
}

export function LoginModal({ isOpen, onClose, initialTab = 'login' }: LoginModalProps) {
  const [activeTab, setActiveTab] = useState<'login' | 'signup'>(initialTab);

  // Reset to initial tab when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  // Close on escape
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <AnimatePresence>
        {isOpen && (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
              />
            </Dialog.Overlay>

            <Dialog.Content asChild>
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                transition={{ type: 'spring', duration: 0.3 }}
                className={cn(
                  'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
                  'w-full max-w-md max-h-[90vh] overflow-y-auto',
                  'bg-gradient-to-br from-slate-800 to-slate-900',
                  'border border-glass-border rounded-2xl',
                  'shadow-2xl shadow-black/40',
                  'z-50'
                )}
              >
                {/* Close button */}
                <Dialog.Close asChild>
                  <button
                    className="absolute top-4 right-4 text-text-muted hover:text-white transition-colors z-10"
                    aria-label="Close"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </Dialog.Close>

                {/* Header with logo */}
                <div className="pt-6 pb-4 px-6 text-center">
                  <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-gradient-to-br from-hud-primary to-teal-600 flex items-center justify-center">
                    <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                    </svg>
                  </div>
                  <Dialog.Title className="text-xl font-bold text-white">
                    {activeTab === 'login' ? 'Welcome Back' : 'Create Account'}
                  </Dialog.Title>
                  <p className="text-sm text-text-secondary mt-1">
                    {activeTab === 'login'
                      ? 'Sign in to access your air quality insights'
                      : 'Join Blurp.ie for cleaner air navigation'}
                  </p>
                </div>

                {/* Tab switcher */}
                <div className="px-6 pb-4">
                  <div className="flex gap-1 p-1 bg-slate-800/50 rounded-xl">
                    <TabButton
                      active={activeTab === 'login'}
                      onClick={() => setActiveTab('login')}
                    >
                      Sign In
                    </TabButton>
                    <TabButton
                      active={activeTab === 'signup'}
                      onClick={() => setActiveTab('signup')}
                    >
                      Sign Up
                    </TabButton>
                  </div>
                </div>

                {/* Form content */}
                <div className="px-6 pb-6">
                  <AnimatePresence mode="wait">
                    {activeTab === 'login' ? (
                      <motion.div
                        key="login"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{ duration: 0.2 }}
                      >
                        <LoginFormInModal onSuccess={onClose} onSwitchToSignup={() => setActiveTab('signup')} />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="signup"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.2 }}
                      >
                        <SignupFormInModal onSuccess={onClose} onSwitchToLogin={() => setActiveTab('login')} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            </Dialog.Content>
          </Dialog.Portal>
        )}
      </AnimatePresence>
    </Dialog.Root>
  );
}

function TabButton({
  active,
  onClick,
  children
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all',
        active
          ? 'bg-hud-primary text-black'
          : 'text-text-secondary hover:text-white'
      )}
    >
      {children}
    </button>
  );
}

// Modified LoginForm for modal context (no Link navigation)
function LoginFormInModal({
  onSuccess,
  onSwitchToSignup
}: {
  onSuccess: () => void;
  onSwitchToSignup: () => void;
}) {
  return (
    <div>
      <LoginForm onSuccess={onSuccess} />
      {/* Override the signup link behavior */}
      <style>{`
        form a[href="/signup"] { display: none; }
      `}</style>
      <p className="text-center text-sm text-text-secondary mt-4">
        Don't have an account?{' '}
        <button
          onClick={onSwitchToSignup}
          className="text-hud-primary hover:text-hud-primary/80 font-medium transition-colors"
        >
          Sign up
        </button>
      </p>
    </div>
  );
}

// Modified SignupForm for modal context
function SignupFormInModal({
  onSuccess,
  onSwitchToLogin
}: {
  onSuccess: () => void;
  onSwitchToLogin: () => void;
}) {
  return (
    <div>
      <SignupForm onSuccess={onSuccess} />
      {/* Override the login link behavior */}
      <style>{`
        form a[href="/login"] { display: none; }
      `}</style>
      <p className="text-center text-sm text-text-secondary mt-4">
        Already have an account?{' '}
        <button
          onClick={onSwitchToLogin}
          className="text-hud-primary hover:text-hud-primary/80 font-medium transition-colors"
        >
          Sign in
        </button>
      </p>
    </div>
  );
}
